import type { PostHog } from "posthog-js";
import { POSTHOG_HOST, POSTHOG_TOKEN } from "@/constants";
import { trackMetaEvent } from "@/lib/metapixel";
import { toAmount } from "@/lib/format";
import type { ContractingItem } from "@/types/domain";

/**
 * Analítica de producto. Se carga con `import()` diferido para que posthog-js no
 * entre al bundle inicial, y queda inerte si no hay llave (en local no se manda
 * nada). El autocapture está apagado: los eventos que importan se instrumentan a
 * mano y el autocapture solo agrega ruido.
 */
let clientPromise: Promise<PostHog | null> | null = null;

export function initPosthog() {
  if (clientPromise || !POSTHOG_TOKEN) return;
  clientPromise = import("posthog-js").then(({ posthog }) => {
    posthog.init(POSTHOG_TOKEN, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      // React Router navega con la History API: así los pageviews de la SPA se
      // capturan solos, sin cablear nada al router.
      capture_pageview: "history_change",
      person_profiles: "identified_only",
    });
    return posthog;
  });
}

/**
 * Evento de producto. No-op mientras no haya token.
 *
 * Es también el único puente hacia el píxel de Meta: los eventos que están en el
 * mapa de `metapixel.ts` viajan también allá. Se hace acá y no en cada pantalla
 * para que instrumentar algo siga siendo una sola línea, y para que exista un solo
 * sitio donde mirar qué se le reporta a la publicidad.
 */
export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  void clientPromise?.then((ph) => ph?.capture(event, properties));
  trackMetaEvent(event, properties);
}

/**
 * Atributos de segmentación de una licitación. El monto va en tramos y en soles
 * (los montos son nativos, por eso el `ratetopen`): agrupar por cifra exacta no
 * sirve de nada, por tramo sí.
 */
export function contractingProps(c: ContractingItem): Record<string, unknown> {
  const amount = c.contracting_contractamount;
  const inSoles =
    amount != null
      ? toAmount(amount) * (c.currency?.currency_ratetopen ?? 1)
      : null;
  return {
    contracting_id: c.contracting_id,
    nomenclature: c.contracting_nomenclature,
    subject: c.contracting_contractsubject,
    stage: c.contractingversion_currentstagenormalized,
    department: c.department?.department_name,
    amount_range: amountRange(inSoles),
    locked: c.isLocked === true,
  };
}

function amountRange(soles: number | null): string {
  if (soles == null) return "desconocido";
  if (soles < 50_000) return "<50k";
  if (soles < 200_000) return "50k-200k";
  if (soles < 1_000_000) return "200k-1M";
  if (soles < 5_000_000) return "1M-5M";
  return ">5M";
}

export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
): void {
  void clientPromise?.then((ph) => ph?.identify(userId, properties));
}

/**
 * Corta la identidad al cerrar sesión. Sin esto, la siguiente cuenta que entre
 * en la misma pestaña hereda la anterior — el mismo motivo por el que el logout
 * hace recarga dura.
 */
export function resetIdentity(): void {
  void clientPromise?.then((ph) => ph?.reset());
}
